/**
 * Drive Chooser Service
 * Uses Picsart's built-in Drive Chooser miniapp to select images
 * This is the same approach used in picsart_chat_v3
 */

import { MINIAPP_PACKAGE_IDS } from '@/constants';
import { setDriveChooserOpen } from '@/utils/driveChooserState';

import { getContext, isContextAvailable } from '../miniappContext';

export interface DriveChooserFile {
  id: string;
  name: string;
  sourceUrl: string;
  thumbnailUrl?: string;
  type?: string;
  size?: number;
}

/**
 * Raw file shape returned by the Drive Chooser miniapp. All fields optional —
 * fall back to defaults when normalizing into DriveChooserFile.
 */
interface DriveChooserRawFile {
  id?: string;
  name?: string;
  sourceUrl?: string;
  thumbnailUrl?: string;
  type?: string;
  size?: number;
}

/**
 * Response from `redirectToPackageId(DRIVE_CHOOSER, ...)`. Shape differs
 * between platforms — web nests files under `result.result.files`, mobile
 * returns them at `result.files`.
 */
interface DriveChooserResponse {
  result?: {
    files?: DriveChooserRawFile[];
    result?: {
      files?: DriveChooserRawFile[];
    };
  };
}

export interface DriveChooserResult {
  success: boolean;
  files: DriveChooserFile[];
  error?: string;
}

/**
 * Check if drive chooser is available
 */
export const isDriveChooserAvailable = (): boolean => {
  return isContextAvailable();
};

/**
 * Open the Picsart Drive Chooser miniapp to select images
 * Returns selected files when user completes selection
 *
 * @param options.multiselect - Allow selecting multiple files (default: false)
 * @param options.filters - Content types to show (default: PHOTO only)
 */
export const openDriveChooser = async (options?: { multiselect?: boolean; filters?: string[] }): Promise<DriveChooserResult> => {
  if (!isContextAvailable()) {
    return {
      success: false,
      files: [],
      error: 'Must run inside Picsart Editor',
    };
  }

  const multiselect = options?.multiselect ?? false;
  const filters = options?.filters ?? ['PHOTO'];

  try {
    const context = getContext();

    // Configure the drive chooser via shared storage
    await context.general.sharedStorage.dispatch({
      driveChooser: {
        multiselect,
        filters: filters.join(','),
      },
    });

    // Open the drive chooser miniapp. Flip the global flag so the wardrobe
    // layout can drop its stacking context to z-0 and the chooser overlay
    // gets a clear foreground.
    setDriveChooserOpen(true);
    let resp: DriveChooserResponse | undefined;
    try {
      resp = await context.handlers.redirectToPackageId(MINIAPP_PACKAGE_IDS.DRIVE_CHOOSER, { comeback: true });
    } finally {
      setDriveChooserOpen(false);
    }

    // Handle response - format differs between web and mobile
    const isWeb = context.handlers.isWeb?.() ?? true;
    const actualResult = isWeb ? resp?.result?.result?.files : resp?.result?.files;

    if (Array.isArray(actualResult) && actualResult.length > 0) {
      const files: DriveChooserFile[] = actualResult.map(file => ({
        id: file.id || '',
        name: file.name || 'Unnamed file',
        sourceUrl: file.sourceUrl || '',
        thumbnailUrl: file.thumbnailUrl || '',
        type: file.type || 'file',
        size: file.size || 0,
      }));

      return {
        success: true,
        files,
      };
    }

    // User cancelled or no files selected
    return {
      success: true,
      files: [],
    };
  } catch (error) {
    console.error('[DriveChooser] Error:', error);
    return {
      success: false,
      files: [],
      error: error instanceof Error ? error.message : 'Failed to open Drive',
    };
  }
};

/**
 * Open drive chooser and return a single image URL
 * Convenience wrapper for single-select use case
 */
export const selectImageFromDrive = async (): Promise<string | null> => {
  const result = await openDriveChooser({ multiselect: false, filters: ['PHOTO'] });

  if (result.success && result.files.length > 0) {
    return result.files[0].sourceUrl;
  }

  return null;
};
